import { useNavigate } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';
import { usePatientProfileContext } from '..';
import {
  CalendarIcon,
  PencilIcon,
  PlusSmIcon,
  ClipboardCheckIcon,
  ArchiveIcon,
  ReplyIcon,
} from '@heroicons/react/outline';
import classNames from 'classnames';
import EmptyProfileTab from '../../../components/EmptyProfileTab';
import IllustrationCalendar from '../../../svgs/IllustrationCalendar';
import StackedAvatars from '../../ClientSessions/components/StackedAvatars';
import { getShortDate } from '../../../lib/time';
import { DateTime } from 'luxon';
import { CLIENT_SESSIONS_ACTIVE_BASE_PATH } from '../../ClientSessions/utils/constants';
import {
  useGetClientSessionsQuery,
  ClientSessionReviewStatus,
  ClientSessionFieldsFragment,
  ClientSession,
} from '../../../../generated/graphql';
import { Transition } from '@headlessui/react';
import { defaultTransitionProps } from '../../../lib/animation';
import Spinner from '../../../svgs/Spinner';
import { customToast } from '../../../components/ToastAlert/customToast';
import TableHeader from '../../../components/TableHeader';
import { ColumnHeaderType } from '../../../types/tables';
import IconButton from '../../../components/IconButton';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox';
import CreateSessionModal from '../../ClientSessions/components/modals/CreateSessionModal';
import Pagination from '../../ClientSessions/components/Pagination';
import { createSessionActions } from '../../ClientSessions/utils/sessionActions';
import DismissModal from '../../ClientSessions/components/modals/DismissModal';
import RestoreModal from '../../ClientSessions/components/modals/RestoreModal';

const columns: ColumnHeaderType[] = [
  {
    field: 'title',
    headerName: 'Title',
    isSortable: false,
  },
  {
    field: 'practitioners',
    headerName: 'Practitioner(s)',
    isSortable: false,
  },
  {
    field: 'dateTime',
    headerName: 'Date & Time',
    isSortable: false,
  },
  {
    field: 'status',
    headerName: 'Status',
    isSortable: false,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    isSortable: false,
  },
];

const getStatusBadgeStyles = (status: ClientSessionReviewStatus) => {
  switch (status) {
    case ClientSessionReviewStatus.PendingReview:
      return 'bg-orange-25';
    case ClientSessionReviewStatus.Confirmed:
      return 'bg-green-25';
    case ClientSessionReviewStatus.Dismissed:
      return 'bg-neutral-50';
    default:
      return 'bg-neutral-50';
  }
};

const getStatusLabel = (status: ClientSessionReviewStatus) => {
  switch (status) {
    case ClientSessionReviewStatus.PendingReview:
      return 'Pending Review';
    case ClientSessionReviewStatus.Confirmed:
      return 'Confirmed';
    case ClientSessionReviewStatus.Dismissed:
      return 'Dismissed';
    default:
      return status;
  }
};

const StatusBadge: React.FC<{ status: ClientSessionReviewStatus }> = ({
  status,
}) => {
  return (
    <div
      className={classNames(
        'inline-flex items-center justify-center rounded-full px-2 py-0.5 text-caption text-neutral-150',
        getStatusBadgeStyles(status),
      )}
    >
      {getStatusLabel(status)}
    </div>
  );
};

const SessionRow: React.FC<{
  session: ClientSessionFieldsFragment;
  onDismiss?: (session: ClientSessionFieldsFragment) => void;
  onRestore?: (session: ClientSessionFieldsFragment) => void;
}> = ({ session, onDismiss, onRestore }) => {
  const navigate = useNavigate();
  const sessionActions = createSessionActions(session);

  // Use ClipboardCheckIcon for pending review, PencilIcon for confirmed/dismissed
  const ActionIcon =
    session.reviewStatus === ClientSessionReviewStatus.PendingReview
      ? ClipboardCheckIcon
      : PencilIcon;

  // Only allow navigation if the session can be edited (not dismissed)
  const handleRowClick = () => {
    if (sessionActions.canEdit()) {
      navigate(`${CLIENT_SESSIONS_ACTIVE_BASE_PATH}/${session.id}`);
    }
  };

  return (
    <tr
      className={classNames(
        'h-[72px] text-ellipsis text-caption text-green-150',
        sessionActions.canEdit() ? 'cursor-pointer hover:bg-neutral-25' : '',
      )}
      onClick={handleRowClick}
    >
      <td className="max-w-xs">
        <div className="-mt-px flex h-12 items-center">
          <div className="flex items-center gap-4">
            <CalendarIcon className="h-6 w-6 text-neutral-125" />
            <div className="flex min-w-[110px] items-center overflow-hidden truncate text-caption">
              {session.title}
            </div>
          </div>
        </div>
      </td>
      <td className="min-w-[100px] max-w-[220px]">
        <div className="-mt-px flex h-12 items-center">
          <StackedAvatars
            items={
              session.practitioners?.map((practitioner) => ({
                name: practitioner.name,
                imageUrl: practitioner.profileImageMedia?.url,
              })) ?? []
            }
          />
        </div>
      </td>
      <td className="min-w-[110px]">
        <div className="-mt-px flex h-12 flex-col items-start justify-center">
          <div className="text-caption font-medium line-clamp-1">
            {getShortDate(session.startTime)}
          </div>
          <div className="text-small-caption text-neutral-125 line-clamp-2">
            {(() => {
              const startDt = DateTime.fromISO(session.startTime);
              const endDt = DateTime.fromISO(session.endTime);
              const startFormatted = startDt.isValid
                ? startDt.toLocaleString({
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })
                : session.startTime;
              const endFormatted = endDt.isValid
                ? endDt.toLocaleString({
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                  })
                : session.endTime;
              return `${startFormatted} - ${endFormatted}`;
            })()}
          </div>
        </div>
      </td>
      <td className="min-w-[140px]">
        <div className="-mt-px flex h-12 items-center">
          <StatusBadge status={session.reviewStatus} />
        </div>
      </td>
      <td className="min-w-[120px]">
        <div className="-mt-px flex h-12 items-center gap-2">
          {sessionActions.canEdit() && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${CLIENT_SESSIONS_ACTIVE_BASE_PATH}/${session.id}`);
              }}
              className="rounded-full bg-neutral-50 p-3 hover:bg-neutral-100/75"
            >
              <ActionIcon className="h-5 w-5 text-neutral-125" />
            </button>
          )}
          {sessionActions.canDismiss() && onDismiss && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(session);
              }}
              className="rounded-full bg-neutral-50 p-3 hover:bg-neutral-100/75"
            >
              <ArchiveIcon className="h-5 w-5 text-neutral-125" />
            </button>
          )}
          {sessionActions.canRestore() && onRestore && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRestore(session);
              }}
              className="rounded-full bg-neutral-50 p-3 hover:bg-neutral-100/75"
            >
              <ReplyIcon className="h-5 w-5 text-neutral-125" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

const Sessions = () => {
  const { patient } = usePatientProfileContext();
  const [isCreateSessionModalOpen, setIsCreateSessionModalOpen] =
    useState(false);
  const [dismissSession, setDismissSession] =
    useState<ClientSessionFieldsFragment | null>(null);
  const [isDismissModalOpen, setIsDismissModalOpen] = useState(false);
  const [restoreSession, setRestoreSession] =
    useState<ClientSessionFieldsFragment | null>(null);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hideDismissedSessions, setHideDismissedSessions] = useState(true);
  const PAGE_SIZE = 50; // Reasonable page size for patient profile

  const { data, loading, error, refetch } = useGetClientSessionsQuery({
    variables: {
      input: {
        filter: {
          patientId: patient?.id,
          // Don't filter by reviewStatus - show all sessions (pending, confirmed, dismissed)
        },
        page: currentPage,
        pageSize: PAGE_SIZE,
      },
    },
    skip: !patient?.id,
    onError: (error) => {
      console.error('Error fetching sessions:', error);
      customToast.error('Unable to fetch sessions.');
    },
  });

  const { upcomingSessions, pastSessions } = useMemo(() => {
    const sessions = data?.clientSessions.edges || [];
    const now = new Date();

    const upcoming: ClientSessionFieldsFragment[] = [];
    const past: ClientSessionFieldsFragment[] = [];

    sessions.forEach((session) => {
      // Filter out dismissed sessions if hideDismissedSessions is true
      if (
        hideDismissedSessions &&
        session.reviewStatus === ClientSessionReviewStatus.Dismissed
      ) {
        return;
      }

      const startTime = new Date(session.startTime);
      if (startTime > now) {
        upcoming.push(session);
      } else {
        past.push(session);
      }
    });

    // Sort upcoming: soonest first (ascending)
    upcoming.sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    );

    // Sort past: most recent first (descending)
    past.sort(
      (a, b) =>
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime(),
    );

    return {
      upcomingSessions: upcoming,
      pastSessions: past,
    };
  }, [data?.clientSessions.edges, hideDismissedSessions]);

  const hasSessions = upcomingSessions.length > 0 || pastSessions.length > 0;

  const handleCreateSessionSuccess = async () => {
    // Reset to first page and refetch sessions data to show the newly created session
    setCurrentPage(1);
    await refetch();
    setIsCreateSessionModalOpen(false);
  };

  const handleCreateSessionClose = () => {
    setIsCreateSessionModalOpen(false);
  };

  const handleDismissSession = (session: ClientSessionFieldsFragment) => {
    setDismissSession(session);
    setIsDismissModalOpen(true);
  };

  const handleDismissSuccess = async () => {
    await refetch();
    setIsDismissModalOpen(false);
    setDismissSession(null);
  };

  const handleDismissClose = () => {
    setIsDismissModalOpen(false);
    setDismissSession(null);
  };

  const handleRestoreSession = (session: ClientSessionFieldsFragment) => {
    setRestoreSession(session);
    setIsRestoreModalOpen(true);
  };

  const handleRestoreSuccess = async () => {
    await refetch();
    setIsRestoreModalOpen(false);
    setRestoreSession(null);
  };

  const handleRestoreClose = () => {
    setIsRestoreModalOpen(false);
    setRestoreSession(null);
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  // Reset to page 1 when patient changes
  useEffect(() => {
    setCurrentPage(1);
  }, [patient?.id]);

  return (
    <>
      {isCreateSessionModalOpen && (
        <CreateSessionModal
          onClose={handleCreateSessionClose}
          onSuccess={handleCreateSessionSuccess}
          initialPatient={(patient as any) || null}
        />
      )}
      {isDismissModalOpen && dismissSession && (
        <DismissModal
          sessions={[dismissSession as ClientSession]}
          onClose={handleDismissClose}
          onSuccess={handleDismissSuccess}
        />
      )}
      {isRestoreModalOpen && restoreSession && (
        <RestoreModal
          sessions={[restoreSession as ClientSession]}
          onClose={handleRestoreClose}
          onSuccess={handleRestoreSuccess}
        />
      )}
      {loading && <Spinner className="mx-auto mt-8" />}
      <Transition
        show={Boolean(!error && !loading)}
        {...defaultTransitionProps}
        className="w-full pt-4"
      >
        {hasSessions ? (
          <div className="max-h-[calc(100vh-224px)] overflow-y-scroll pb-4">
            <table className="min-w-full transition-all duration-150">
              <thead className="sticky top-0 z-10 h-[40px] w-full">
                <tr className="bg-white">
                  {columns.map((column, i) => {
                    const isLastColumn = i === columns.length - 1;

                    return (
                      <TableHeader
                        key={`sessionsTableColumnHeader_${column.field}`}
                        column={column}
                        isLastColumn={isLastColumn}
                      />
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {/* Upcoming Section */}
                {upcomingSessions.length > 0 && (
                  <>
                    <tr className="border-b border-secondary-100 bg-white">
                      <td
                        colSpan={3}
                        className="px-4 py-3 text-small-caption font-medium text-neutral-150"
                      >
                        <span>Upcoming ({upcomingSessions.length})</span>
                      </td>
                      <td className="px-4 py-3">
                        <Checkbox
                          id="hide-dismissed-sessions-upcoming"
                          checked={hideDismissedSessions}
                          onChange={(e) =>
                            setHideDismissedSessions(e.target.checked)
                          }
                          labelContent="Hide dismissed sessions"
                          className="text-caption"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <IconButton
                            IconComponent={PlusSmIcon}
                            onClick={() => setIsCreateSessionModalOpen(true)}
                            aria-label="Create Session"
                            className="!rounded-full bg-neutral-50 p-2 hover:bg-neutral-100/75"
                            iconClassName="h-5 w-5 text-neutral-125"
                          />
                        </div>
                      </td>
                    </tr>
                    {upcomingSessions.map((session) => (
                      <SessionRow
                        key={session.id}
                        session={session}
                        onDismiss={handleDismissSession}
                        onRestore={handleRestoreSession}
                      />
                    ))}
                  </>
                )}

                {/* Past Section */}
                {pastSessions.length > 0 && (
                  <>
                    <tr className="border-b border-secondary-100 bg-white">
                      <td
                        colSpan={3}
                        className="px-4 py-3 text-small-caption font-medium text-neutral-150"
                      >
                        <span>Past ({pastSessions.length})</span>
                      </td>
                      <td className="px-4 py-3">
                        <Checkbox
                          id="hide-dismissed-sessions-past"
                          checked={hideDismissedSessions}
                          onChange={(e) =>
                            setHideDismissedSessions(e.target.checked)
                          }
                          labelContent="Hide dismissed sessions"
                          className="text-caption"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <IconButton
                            IconComponent={PlusSmIcon}
                            onClick={() => setIsCreateSessionModalOpen(true)}
                            aria-label="Create Session"
                            className="!rounded-full bg-neutral-50 p-2 hover:bg-neutral-100/75"
                            iconClassName="h-5 w-5 text-neutral-125"
                          />
                        </div>
                      </td>
                    </tr>
                    {pastSessions.map((session) => (
                      <SessionRow
                        key={session.id}
                        session={session}
                        onDismiss={handleDismissSession}
                        onRestore={handleRestoreSession}
                      />
                    ))}
                  </>
                )}
              </tbody>
            </table>
            {data?.clientSessions.pageInfo && (
              <Pagination
                hasNextPage={data.clientSessions.pageInfo.hasNextPage ?? false}
                hasPreviousPage={
                  data.clientSessions.pageInfo.hasPreviousPage ?? false
                }
                totalCount={data.clientSessions.totalCount ?? 0}
                itemCountOnPage={upcomingSessions.length + pastSessions.length}
                onNextPage={handleNextPage}
                onPreviousPage={handlePreviousPage}
              />
            )}
          </div>
        ) : (
          <EmptyProfileTab
            IconComponent={IllustrationCalendar}
            iconClassName={'text-secondary-125'}
            titleText={`${patient?.firstName ?? ''}'s sessions`}
            subTitleText={`All sessions you've scheduled with ${
              patient?.firstName ?? 'this client'
            } will show up here.`}
            ButtonComponent={
              <Button
                title="Create Session"
                theme="primary"
                size="medium"
                onClick={() => setIsCreateSessionModalOpen(true)}
                IconComponent={PlusSmIcon}
                iconPosition="left"
              />
            }
          />
        )}
      </Transition>
    </>
  );
};

export default Sessions;
